import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  DataTable,
  EmptyState,
  ErrorNotice,
  LoadingState,
  PageHeading,
  Panel,
  Pill,
  SearchInput,
  SegmentedControl,
  StatTile,
} from "@bcecloud/portal-ui";
import { errorMessage, getJson } from "@/lib/api";
import { filterRows, sortRows } from "@/lib/facet-table";
import { APP_DOMAIN, APP_NAME, LEGACY_PORTAL_LABEL } from "@/lib/launch";
import { useTableSort } from "@/lib/use-table-sort";
import { useUrlSearch } from "@/lib/use-list-query-state";

/** The applications the adoption report covers, and how to label them. */
const APPS = {
  app: { id: "app", label: APP_NAME, hint: APP_DOMAIN },
  legacy: {
    id: "legacy",
    label: LEGACY_PORTAL_LABEL,
    hint: "www.bcecloud.com",
  },
} as const;

/** An application id in the adoption report. */
type AppId = keyof typeof APPS;

/** The two populations rolling over to the new portal, by role. */
const GROUPS = {
  internal: {
    id: "internal",
    label: "Internal staff",
    roles: new Set(["internal", "admin", "superadmin"]),
  },
  facilitators: {
    id: "facilitators",
    label: "Facilitators",
    roles: new Set(["contractor"]),
  },
} as const;

type GroupId = keyof typeof GROUPS;

/** How the table is filtered. */
const FILTERS = {
  all: "all",
  onboard: "onboard",
  notYet: "notYet",
} as const;

type Filter = (typeof FILTERS)[keyof typeof FILTERS];

/** Sortable columns → the flattened row field each orders by. */
const ADOPTION_SORT_FIELDS = {
  person: "name",
  role: "role",
  status: "statusRank",
  firstSeen: "firstSeenAt",
  lastSeen: "lastSeenAt",
  sessions: "visitCount",
} as const;
const ADOPTION_SORT_KEYS = Object.values(ADOPTION_SORT_FIELDS);

/** The row fields the search box matches. */
const ADOPTION_SEARCH_FIELDS: ReadonlyArray<keyof AdoptionRow> = [
  "name",
  "email",
  "role",
];

/** Default ordering: most recently seen first, so the live users lead. */
const DEFAULT_SORT = {
  key: ADOPTION_SORT_FIELDS.lastSeen,
  direction: "desc",
} as const;

/**
 * Status column order — Active before Lapsed before Not yet — so sorting the
 * pill column groups people by where they are in the rollout.
 */
const STATUS_RANK = { active: 0, lapsed: 1, notYet: 2 } as const;
type Status = keyof typeof STATUS_RANK;

/** One person's usage of one application. */
interface AdoptionUsage {
  readonly firstSeenAt: string;
  readonly lastSeenAt: string;
  readonly visitCount: number;
}

/** One person's adoption row. */
interface AdoptionUser {
  readonly userId: string;
  readonly name: string;
  readonly email: string;
  readonly role: string;
  readonly apps: Partial<Record<AppId, AdoptionUsage>>;
}

/** Headline numbers for one application. */
interface AdoptionSummary {
  readonly app: AppId;
  readonly users: number;
  readonly activeUsers: number;
  readonly visits: number;
}

/** The adoption report as the API returns it. */
interface AdoptionReport {
  readonly activeWindowDays: number;
  readonly summary: readonly AdoptionSummary[];
  readonly users: readonly AdoptionUser[];
}

/**
 * One table row, flattened so every sortable column is a top-level field.
 * ISO timestamps sort correctly as strings; absent usage sorts last.
 */
interface AdoptionRow extends AdoptionUser {
  readonly status: Status;
  readonly statusRank: number;
  readonly firstSeenAt: string | undefined;
  readonly lastSeenAt: string | undefined;
  readonly visitCount: number;
}

/** Flattens a report user into a sortable row. */
function toRow(user: AdoptionUser, activeWindowDays: number): AdoptionRow {
  const usage = user.apps.app;
  const status: Status =
    usage === undefined
      ? "notYet"
      : daysAgo(usage.lastSeenAt) <= activeWindowDays
        ? "active"
        : "lapsed";
  return {
    ...user,
    status,
    statusRank: STATUS_RANK[status],
    firstSeenAt: usage?.firstSeenAt,
    lastSeenAt: usage?.lastSeenAt,
    visitCount: usage?.visitCount ?? 0,
  };
}

/** Formats an ISO timestamp as a short local date, or an em dash when absent. */
function shortDate(iso: string | undefined): string {
  if (iso === undefined) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Whole days between a timestamp and now, for the "last seen" reading. Rounded
 * down, so "today" covers anything under 24 hours.
 */
function daysAgo(iso: string): number {
  return Math.floor(
    (Date.now() - new Date(iso).getTime()) / (24 * 60 * 60 * 1000),
  );
}

/** A relative "last seen" reading — the number people actually scan for. */
function lastSeenLabel(iso: string | undefined): string {
  if (iso === undefined) return "Never";
  const days = daysAgo(iso);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 30) return `${days} days ago`;
  return shortDate(iso);
}

/**
 * Soft-launch adoption: who among internal staff has actually opened BCEcloud,
 * and who is still working entirely in the classic portal.
 *
 * The report deliberately lists staff who have *never* been seen as well as
 * those who have — during a rollout, the people who have not arrived yet are
 * the actionable half, and a report built only from usage rows cannot name
 * them.
 *
 * @returns The adoption page.
 */
export function AdoptionPage(): React.ReactElement {
  const [group, setGroup] = useState<GroupId>("internal");
  const [filter, setFilter] = useState<Filter>(FILTERS.all);
  const { sort, onSort } = useTableSort(ADOPTION_SORT_KEYS, DEFAULT_SORT);
  const { search, setSearch } = useUrlSearch();

  const report = useQuery({
    queryKey: ["adoption"],
    queryFn: ({ signal }) =>
      getJson<AdoptionReport>("/telemetry/adoption", undefined, signal),
    staleTime: 60_000,
  });

  const groupUsers = useMemo(
    () =>
      (report.data?.users ?? []).filter((user) =>
        (GROUPS[group].roles as ReadonlySet<string>).has(user.role),
      ),
    [report.data, group],
  );

  const users = useMemo<readonly AdoptionRow[]>(() => {
    const filtered =
      filter === FILTERS.onboard
        ? groupUsers.filter((user) => user.apps.app !== undefined)
        : filter === FILTERS.notYet
          ? groupUsers.filter((user) => user.apps.app === undefined)
          : groupUsers;
    const rows = filtered.map((user) =>
      toRow(user, report.data?.activeWindowDays ?? 0),
    );
    return sortRows(filterRows(rows, search, ADOPTION_SEARCH_FIELDS), sort);
  }, [groupUsers, report.data, filter, search, sort]);

  if (report.isPending) return <LoadingState label="Loading adoption…" />;
  if (report.isError) {
    return (
      <ErrorNotice
        title="Adoption unavailable"
        message={errorMessage(report.error)}
      />
    );
  }

  const staffCount = groupUsers.length;
  const onboard = groupUsers.filter(
    (user) => user.apps.app !== undefined,
  ).length;
  const activeCount = groupUsers.filter(
    (user) =>
      user.apps.app !== undefined &&
      daysAgo(user.apps.app.lastSeenAt) <= report.data.activeWindowDays,
  ).length;
  const groupSessions = groupUsers.reduce(
    (total, user) => total + (user.apps.app?.visitCount ?? 0),
    0,
  );
  const countOf = (id: GroupId): number =>
    (report.data?.users ?? []).filter((user) =>
      (GROUPS[id].roles as ReadonlySet<string>).has(user.role),
    ).length;

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="SOFT LAUNCH"
        title="Adoption"
        subtitle={`Which portal internal staff and facilitators are working in. "Active" means seen in the last ${report.data.activeWindowDays} days.`}
      />

      <div className="mb-4">
        <SegmentedControl<GroupId>
          label="Group"
          value={group}
          onChange={(next) => {
            setGroup(next);
            setFilter(FILTERS.all);
          }}
          options={(Object.keys(GROUPS) as GroupId[]).map((id) => ({
            key: id,
            label: `${GROUPS[id].label} (${countOf(id)})`,
          }))}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <StatTile
          label={GROUPS[group].label}
          value={String(staffCount)}
          hint="Active accounts"
        />
        <StatTile
          label={`Signed in to ${APP_NAME}`}
          value={`${onboard} of ${staffCount}`}
          hint={APP_DOMAIN}
        />
        <StatTile
          label={`Active in the last ${report.data.activeWindowDays} days`}
          value={String(activeCount)}
          hint={`${groupSessions} sessions in total`}
        />
      </div>

      <div className="mt-6 mb-4 flex flex-wrap items-center gap-3">
        <SearchInput
          label="Search people"
          placeholder="Name, email or role"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="w-full max-w-sm"
        />
        <SegmentedControl<Filter>
          label="Which people to show"
          value={filter}
          onChange={setFilter}
          options={[
            { key: FILTERS.all, label: `Everyone (${staffCount})` },
            { key: FILTERS.onboard, label: `On ${APP_NAME} (${onboard})` },
            { key: FILTERS.notYet, label: `Not yet (${staffCount - onboard})` },
          ]}
        />
      </div>

      {users.length === 0 ? (
        search.trim() !== "" ? (
          <Panel>
            <EmptyState title="No people match" message="Try a different search." />
          </Panel>
        ) : (
          <EmptyState
            title="Nobody here"
            message={`No ${GROUPS[group].label.toLowerCase()} match this filter.`}
          />
        )
      ) : (
        <DataTable
          headers={[
            { label: "Person", sortKey: ADOPTION_SORT_FIELDS.person },
            { label: "Role", sortKey: ADOPTION_SORT_FIELDS.role },
            { label: APPS.app.label, sortKey: ADOPTION_SORT_FIELDS.status },
            { label: "First seen", sortKey: ADOPTION_SORT_FIELDS.firstSeen },
            { label: "Last seen", sortKey: ADOPTION_SORT_FIELDS.lastSeen },
            { label: "Sessions", sortKey: ADOPTION_SORT_FIELDS.sessions },
          ]}
          rightAlignedHeaders={[5]}
          sort={sort}
          onSort={onSort}
          minWidth={860}
        >
          {users.map((user) => (
            <tr
              key={user.userId}
              className="border-t border-portal-line align-top"
            >
              <td className="p-3 text-portal-small">
                <span className="block font-bold text-portal-ink">
                  {user.name}
                </span>
                <span className="block text-portal-micro text-portal-muted">
                  {user.email}
                </span>
              </td>
              <td className="p-3 text-portal-small text-portal-muted">
                {user.role}
              </td>
              <td className="p-3 text-portal-small">
                {user.status === "notYet" ? (
                  <Pill tone="neutral">Not yet</Pill>
                ) : user.status === "active" ? (
                  <Pill tone="success">Active</Pill>
                ) : (
                  <Pill tone="warning">Lapsed</Pill>
                )}
              </td>
              <td className="p-3 text-portal-small whitespace-nowrap text-portal-muted">
                {shortDate(user.firstSeenAt)}
              </td>
              <td className="p-3 text-portal-small whitespace-nowrap text-portal-muted">
                {lastSeenLabel(user.lastSeenAt)}
              </td>
              <td className="p-3 text-right text-portal-small text-portal-muted">
                {user.visitCount}
              </td>
            </tr>
          ))}
        </DataTable>
      )}

      <p className="mt-4 text-portal-micro text-portal-muted">
        {APPS.legacy.label} usage appears here once the classic portal reports
        it — the endpoint already accepts it, so no change is needed on this
        side.
      </p>
    </section>
  );
}
